import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from '@/hooks/useWebSocket';
import ShareClipsSheet from '@/components/teleop/ShareClipsSheet';
import TeleopBottomDock from '@/components/teleop/TeleopBottomDock';
import TeleopCameraControls from '@/components/teleop/TeleopCameraControls';
import TeleopControlPad from '@/components/teleop/TeleopControlPad';
import TeleopHud from '@/components/teleop/TeleopHud';
import TeleopPromptOverlay from '@/components/teleop/TeleopPromptOverlay';
import TeleopVideoSurface from '@/components/teleop/TeleopVideoSurface';
import { logInfo, logWarn } from '@/lib/logger';
import { isVideoDebugEnabledFromLocation } from '@/lib/videoDebug';
import { isEarlyAccessPilotEnvironment } from '@/lib/earlyAccessPilot';
import { isFirefoxBrowser } from '@/lib/videoFraming';
import { TELEOP_SETTINGS } from './teleopConfig';
import type { TeleopAttemptConfig } from './teleopAttemptTypes';
import type { TeleopMotionConfig } from './teleopMotionTypes';
import { useTeleopAttemptFlow } from './useTeleopAttemptFlow';
import { useTeleopCameraSession } from './useTeleopCameraSession';
import { useTeleopMotionController } from './useTeleopMotionController';
import { useTeleopMotionInput } from './useTeleopMotionInput';
import { useTeleopSelfie } from './useTeleopSelfie';
import { useTeleopViewportLayout } from './useTeleopViewportLayout';

type TeleopProps = {
  sessionId: string;
  robotId: string;
  roomId: string;
  queueConnected: boolean;
  onPlayAgain?: () => void;
  onReleaseSession?: () => void | Promise<void>;
};

const {
  joystick: {
    maxDist: MAX_DIST,
    keyRepeatMs: KEY_REPEAT_MS,
  },
  rotation: { keyboardVelocity: KEYBOARD_ROTATION_VELOCITY },
  video: {
    source: {
      widthPx: SOURCE_VIDEO_WIDTH_PX,
      heightPx: SOURCE_VIDEO_HEIGHT_PX,
    },
    viewport: {
      mobileMaxWidthPx: MOBILE_MAX_WIDTH_PX,
      tabletMaxWidthPx: TABLET_MAX_WIDTH_PX,
      cameraProfiles: {
        overhead: OVERHEAD_VIDEO_VIEWPORT,
        perspective: PERSPECTIVE_VIDEO_VIEWPORT,
      },
    },
  },
} = TELEOP_SETTINGS;
const CAMERA_CONFIG = TELEOP_SETTINGS.camera;
const ATTEMPT_CONFIG: TeleopAttemptConfig = TELEOP_SETTINGS.attempts;
const MOTION_CONFIG: TeleopMotionConfig = {
  motionSendIntervalMs: TELEOP_SETTINGS.joystick.motionSendIntervalMs,
  motionHorizonMs: TELEOP_SETTINGS.joystick.motionHorizonMs,
};
const MAX_ATTEMPTS = ATTEMPT_CONFIG.maxAttempts;
type ConfettiLauncher = { (options?: Record<string, unknown>): Promise<unknown>; reset?: () => void };
type ConfettiModule = {
  create: (target: HTMLCanvasElement, opts: { resize: boolean; useWorker: boolean }) => ConfettiLauncher;
  default?: {
    create: (target: HTMLCanvasElement, opts: { resize: boolean; useWorker: boolean }) => ConfettiLauncher;
  };
};

const Teleop: React.FC<TeleopProps> = ({
  sessionId,
  robotId,
  roomId,
  queueConnected,
  onPlayAgain,
  onReleaseSession,
}) => {
  const { sendMessage, connected, addMessageListener } = useWebSocket();
  const shouldShowEarlyAccessPilot = isEarlyAccessPilotEnvironment(import.meta.env.MODE);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const joystickRef = useRef<HTMLDivElement | null>(null);
  const zAreaRef = useRef<HTMLDivElement | null>(null);

  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);
  const debugWs = import.meta.env.VITE_WS_DEBUG === 'true';
  const [showDebugPanel, setShowDebugPanel] = useState<boolean>(Boolean(import.meta.env.DEV));
  const [videoDebugEnabled] = useState<boolean>(() => isVideoDebugEnabledFromLocation());
  const [isFirefox] = useState<boolean>(() => isFirefoxBrowser());
  const confettiLayerRef = useRef<HTMLDivElement | null>(null);
  const confettiCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const confettiCleanupRef = useRef<number | null>(null);
  const sendGrabProxyRef = useRef<() => boolean>(() => false);
  const {
    livekitRoom,
    livekitRoomInstance,
    webrtcStatus,
    activeCamera,
    canSwitchCamera,
    switchToSecondary,
    switchToOverhead,
    triggerCameraSwitch,
    handleTracksChange,
    handleActiveTrackChange,
    handleLivekitRoom,
    handleWebrtcStatusChange,
    handleVideoPointerDown,
    handleVideoPointerUp,
    handleVideoPointerCancel,
    handleVideoWheel,
  } = useTeleopCameraSession({
    roomId,
    robotId,
    debugWs,
    config: CAMERA_CONFIG,
  });
  const {
    containerRef,
    viewportSize,
    activeViewportProfile,
    activeVideoViewportBoundingBox,
    videoStyle,
    isMobileLandscapeMode,
    canShowDesktopSelfieHideControl,
    showDesktopRotationKeyboardHint,
    cameraControlsPositionClass,
    earlyAccessPilotPositionClass,
  } = useTeleopViewportLayout({
    activeCamera,
    secondaryTrack: CAMERA_CONFIG.secondaryTrack,
    mobileMaxWidthPx: MOBILE_MAX_WIDTH_PX,
    tabletMaxWidthPx: TABLET_MAX_WIDTH_PX,
    sourceWidthPx: SOURCE_VIDEO_WIDTH_PX,
    sourceHeightPx: SOURCE_VIDEO_HEIGHT_PX,
    overheadVideoViewport: OVERHEAD_VIDEO_VIEWPORT,
    perspectiveVideoViewport: PERSPECTIVE_VIDEO_VIEWPORT,
  });

  const formatSeconds = useCallback((totalSeconds: number) => {
    const safeSeconds = Math.max(0, totalSeconds);
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    if (!debugWs) {
      return;
    }
    logInfo('teleop.ws.status', 'ws status', { connected, robotId, sessionId });
  }, [connected, debugWs, robotId, sessionId]);

  useEffect(() => {
    setIsShareSheetOpen(false);
  }, [sessionId]);

  const sendGrab = useCallback(() => {
    const currentSendGrab = sendGrabProxyRef.current;
    const sent = currentSendGrab();

    return sent;
  }, []);

  const {
    attemptResult,
    postAttemptPrompt,
    bonusAttemptPrompt,
    attemptPhase,
    attemptIndex,
    secondsLeft,
    awaitingResult,
    readyMessage,
    countdownValue,
    lastWsMeta,
    sessionEventLog,
    controlsEnabled,
    handlePlayAgain: resetAttemptFlowForPlayAgain,
    handleBonusAttemptOk: acknowledgeBonusAttemptPrompt,
    triggerGrab,
  } = useTeleopAttemptFlow({
    sessionId,
    connected,
    debugWs,
    showDebugPanel,
    config: ATTEMPT_CONFIG,
    sendMessage,
    addMessageListener,
    sendGrab,
    switchToOverhead,
    onPlayAgain,
    onReleaseSession,
  });

  const {
    updateDesiredMotion,
    sendGrab: sendGrabFromMotionController,
  } = useTeleopMotionController({
    sessionId,
    robotId,
    connected,
    controlsEnabled,
    debugWs,
    config: MOTION_CONFIG,
    sendMessage,
    onGrabAttempted: switchToSecondary,
  });

  sendGrabProxyRef.current = sendGrabFromMotionController;

  const handlePlayAgain = useCallback(() => {
    setIsShareSheetOpen(false);
    resetAttemptFlowForPlayAgain();
  }, [resetAttemptFlowForPlayAgain]);

  const canTriggerGrabWithKeyboard =
    attemptPhase === 'attempt' &&
    connected &&
    Boolean(robotId);
  const bonusPromptVisible = bonusAttemptPrompt !== null;
  const resultPromptVisible =
    postAttemptPrompt !== null &&
    !isShareSheetOpen;

  const {
    knobOffset,
    zAngle,
  } = useTeleopMotionInput({
    joystickRef,
    zAreaRef,
    maxDist: MAX_DIST,
    keyRepeatMs: KEY_REPEAT_MS,
    keyboardRotationVelocity: KEYBOARD_ROTATION_VELOCITY,
    updateDesiredMotion,
    triggerGrab,
    canTriggerGrabWithKeyboard,
    bonusPromptVisible,
    resultPromptVisible,
    acknowledgeBonusAttemptPrompt,
    handlePlayAgain,
  });
  const {
    selfieStream,
    selfieEnabled,
    selfiePreviewVisible,
    selfieSupported,
    selfieNotice,
    disableSelfie,
    handleSelfieToggle,
    hideSelfiePreview,
    showSelfiePreview,
  } = useTeleopSelfie({
    sessionId,
    livekitRoomInstance,
  });

  const clearConfetti = useCallback(() => {
    if (confettiCleanupRef.current !== null) {
      window.clearTimeout(confettiCleanupRef.current);
      confettiCleanupRef.current = null;
    }
    if (confettiCanvasRef.current && confettiCanvasRef.current.parentNode) {
      confettiCanvasRef.current.parentNode.removeChild(confettiCanvasRef.current);
    }
    confettiCanvasRef.current = null;
  }, []);

  const fireConfetti = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const forceMotion = document.documentElement.dataset.forceMotion === 'true';
    if (prefersReducedMotion && !forceMotion) {
      if (debugWs) {
        logInfo('teleop.confetti.skipped', 'confetti skipped (reduced motion)');
      }
      return;
    }
    const layer = confettiLayerRef.current;
    if (!layer) {
      return;
    }

    clearConfetti();

    const canvas = document.createElement('canvas');
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100vw';
    canvas.style.height = '100vh';
    canvas.style.zIndex = '40';
    canvas.style.pointerEvents = 'none';
    const width = window.innerWidth;
    const height = window.innerHeight;
    document.body.appendChild(canvas);
    confettiCanvasRef.current = canvas;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(width * dpr));
    canvas.height = Math.max(1, Math.floor(height * dpr));

    let confettiInstance:
      | ConfettiLauncher
      | null = null;
    try {
      const module = await import('canvas-confetti');
      const confettiModule = module as ConfettiModule;
      const confetti = confettiModule.default ?? confettiModule;
      confettiInstance = confetti.create(canvas, { resize: true, useWorker: true });
    } catch (error) {
      if (debugWs) {
        logWarn('teleop.confetti.import_failed', 'confetti import failed', { error: String(error) });
      }
      return;
    }

    const burst = (origin: { x: number; y: number }) => {
      confettiInstance?.({
        particleCount: 260,
        spread: 80,
        angle: 90,
        startVelocity: 40,
        ticks: 2400,
        gravity: 1.35,
        decay: 0.9,
        scalar: 2.2,
        origin,
      });
    };

    burst({ x: 0.5, y: 0.22 });
    window.setTimeout(() => burst({ x: 0.2, y: 0.22 }), 320);
    window.setTimeout(() => burst({ x: 0.8, y: 0.22 }), 640);

    confettiCleanupRef.current = window.setTimeout(() => {
      confettiCleanupRef.current = null;
      confettiInstance?.reset?.();
      if (confettiCanvasRef.current && confettiCanvasRef.current.parentNode) {
        confettiCanvasRef.current.parentNode.removeChild(confettiCanvasRef.current);
      }
      confettiCanvasRef.current = null;
    }, 5200);
  }, [clearConfetti, debugWs]);

  useEffect(() => {
    if (!attemptResult?.success) {
      return;
    }
    fireConfetti();
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const forceMotion = document.documentElement.dataset.forceMotion === 'true';
    if ((!prefersReducedMotion || forceMotion) && navigator.vibrate) {
      navigator.vibrate(25);
      window.setTimeout(() => navigator.vibrate(25), 70);
    }
  }, [attemptResult, fireConfetti]);

  useEffect(() => {
    return () => {
      clearConfetti();
    };
  }, [clearConfetti]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }

      if (event.ctrlKey && event.altKey && (event.key === 'd' || event.key === 'D')) {
        if (event.repeat) {
          return;
        }

        event.preventDefault();
        setShowDebugPanel((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const livekitStatus =
    webrtcStatus === 'connected'
      ? 'connected'
      : webrtcStatus === 'connecting'
        ? 'connecting'
        : 'disconnected';
  const controlPlaneStatus = connected ? 'connected' : 'disconnected';
  const queueStatus = queueConnected ? 'connected' : 'disconnected';
  const shakeStatus = Boolean(attemptResult && !attemptResult.success);
  const attemptSeq = attemptResult?.seq ?? 0;
  const shakeStamp = attemptSeq % 1000;
  const shakeAnimation = shakeStatus ? `attempt-shake 420ms ease-out ${shakeStamp}ms` : 'none';

  const handleShare = () => {
    setIsShareSheetOpen(true);
  };
  const teleopRootClassName = isFirefox
    ? 'firefox-live-video-ui relative h-full min-h-[var(--app-height)] h-[var(--app-height)] w-full select-none overflow-hidden bg-background text-foreground touch-none pb-[env(safe-area-inset-bottom)]'
    : 'relative h-full min-h-[var(--app-height)] h-[var(--app-height)] w-full select-none overflow-hidden bg-background text-foreground touch-none pb-[env(safe-area-inset-bottom)]';

  return (
    <div
      ref={containerRef}
      className={teleopRootClassName}
    >
      <TeleopVideoSurface
        confettiLayerRef={confettiLayerRef}
        videoRef={videoRef}
        videoStyle={videoStyle}
        onVideoPointerDown={handleVideoPointerDown}
        onVideoPointerUp={handleVideoPointerUp}
        onVideoPointerCancel={handleVideoPointerCancel}
        onVideoWheel={handleVideoWheel}
        livekitRoom={livekitRoom}
        activeCamera={activeCamera}
        onStatusChange={handleWebrtcStatusChange}
        onTracksChange={handleTracksChange}
        onActiveTrackChange={handleActiveTrackChange}
        onRoom={handleLivekitRoom}
        videoDebugEnabled={videoDebugEnabled}
        viewportWidthPx={viewportSize.widthPx}
        viewportHeightPx={viewportSize.heightPx}
        viewportProfile={activeViewportProfile}
        criticalBoundingBox={activeVideoViewportBoundingBox}
        expectedStyle={videoStyle}
      />
      <TeleopHud
        queueStatus={queueStatus}
        controlPlaneStatus={controlPlaneStatus}
        livekitStatus={livekitStatus}
        shakeAnimation={shakeAnimation}
        shouldShowEarlyAccessPilot={shouldShowEarlyAccessPilot}
        earlyAccessPilotPositionClass={earlyAccessPilotPositionClass}
        attemptPhase={attemptPhase}
        attemptIndex={attemptIndex}
        maxAttempts={MAX_ATTEMPTS}
        secondsLeft={secondsLeft}
        awaitingResult={awaitingResult}
        readyMessage={readyMessage}
        showDebugPanel={showDebugPanel}
        lastWsMeta={lastWsMeta}
        sessionId={sessionId}
        sessionEventLog={sessionEventLog}
        formatSeconds={formatSeconds}
      />
      <TeleopPromptOverlay
        attemptPhase={attemptPhase}
        countdownValue={countdownValue}
        attemptResult={attemptResult}
        readyMessage={readyMessage}
        awaitingResult={awaitingResult}
        postAttemptPrompt={postAttemptPrompt}
        bonusAttemptPrompt={bonusAttemptPrompt}
        onPlayAgain={handlePlayAgain}
        onShare={handleShare}
        onAcknowledgeBonusAttempt={acknowledgeBonusAttemptPrompt}
      />
      {isShareSheetOpen && postAttemptPrompt ? (
        <ShareClipsSheet sessionId={sessionId} onClose={() => setIsShareSheetOpen(false)} />
      ) : null}
      <TeleopCameraControls
        activeCamera={activeCamera}
        canSwitchCamera={canSwitchCamera}
        positionClassName={cameraControlsPositionClass}
        onSwitchPrevious={() => triggerCameraSwitch('prev')}
        onSwitchNext={() => triggerCameraSwitch('next')}
      />
      <TeleopControlPad
        zAreaRef={zAreaRef}
        joystickRef={joystickRef}
        controlsEnabled={controlsEnabled}
        showDesktopRotationKeyboardHint={showDesktopRotationKeyboardHint}
        shakeAnimation={shakeAnimation}
        zAngle={zAngle}
        knobOffset={knobOffset}
      />
      <TeleopBottomDock
        isMobileLandscapeMode={isMobileLandscapeMode}
        selfieStream={selfieStream}
        selfieEnabled={selfieEnabled}
        selfiePreviewVisible={selfiePreviewVisible}
        selfieSupported={selfieSupported}
        selfieNotice={selfieNotice}
        canShowDesktopSelfieHideControl={canShowDesktopSelfieHideControl}
        onDisableSelfie={disableSelfie}
        onToggleSelfie={handleSelfieToggle}
        onHideSelfie={hideSelfiePreview}
        onShowSelfie={showSelfiePreview}
        controlsEnabled={controlsEnabled}
        onTriggerGrab={triggerGrab}
      />
    </div>
  );
};

export default Teleop;
